import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  LinearProgress,
  Typography
} from '@mui/material'
import { TrendingUp, CheckCircle } from '@mui/icons-material'

const CampaignProgressBar = ({ campaign, firms = [] }) => {
  const { t } = useTranslation()

  const { progress, totalCount, processedCount, shouldShow, statusType } = useMemo(() => {
    if (!campaign) {
      return { progress: 0, totalCount: 0, processedCount: 0, shouldShow: false }
    }

    const status = campaign.status

    if (status === 'READY_TO_SELECT') {
      const totalCount = firms.length || 1

      return {
        progress: 100,
        processedCount: totalCount,
        totalCount,
        shouldShow: true,
        statusType: 'preparing'
      }
    }

    if (status === 'READY_TO_MAIL') {
      const selectedFirms = firms.filter(firm => firm.selected === true)
      const totalCount = selectedFirms.length || 1

      return {
        progress: 100,
        processedCount: totalCount,
        totalCount,
        shouldShow: true,
        statusType: 'generating'
      }
    }

    if (!firms.length) {
      if (status === 'PREPARING' || status === 'GENERATING') {
        return {
          progress: 0,
          totalCount: 0,
          processedCount: 0,
          shouldShow: true,
          statusType: status === 'PREPARING' ? 'preparing' : 'generating'
        }
      }

      return { progress: 0, processedCount: 0, totalCount: 0, shouldShow: false }
    }

    if (status === 'PREPARING') {
      const totalCount = firms.length
      const processedCount = firms.filter(firm =>
        firm.explanation &&
        firm.isRelevant !== null && firm.isRelevant !== undefined
      ).length

      return {
        progress: totalCount > 0 ? (processedCount / totalCount) * 100 : 0,
        processedCount,
        totalCount,
        shouldShow: true,
        statusType: 'preparing'
      }
    }

    if (status === 'GENERATING') {
      const selectedFirms = firms.filter(firm => firm.selected === true)
      const totalCount = selectedFirms.length
      const processedCount = selectedFirms.filter(firm =>
        firm.emailContent !== null && firm.emailContent !== undefined &&
        firm.emailSubject !== null && firm.emailSubject !== undefined
      ).length

      return {
        progress: totalCount > 0 ? (processedCount / totalCount) * 100 : 0,
        processedCount,
        totalCount,
        shouldShow: true,
        statusType: 'generating'
      }
    }

    return { progress: 0, processedCount: 0, totalCount: 0, shouldShow: false }
  }, [campaign, firms])

  if (!shouldShow) {
    return null
  }

  const isComplete = progress === 100

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {isComplete
          ? (
            <CheckCircle sx={{ color: 'success.main', mr: 1.5, fontSize: '1.25rem' }}/>
            )
          : (
            <TrendingUp sx={{ color: 'primary.main', mr: 1.5, fontSize: '1.25rem' }}/>
            )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: isComplete ? 'success.main' : 'primary.main'
          }}
        >
          {statusType === 'preparing'
            ? t('mailTemplate.campaigns.progress.preparingTitle')
            : t('mailTemplate.campaigns.progress.generatingTitle')
          }
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {statusType === 'preparing'
            ? t('mailTemplate.campaigns.progress.preparingDescription')
            : t('mailTemplate.campaigns.progress.generatingDescription')
          }
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: isComplete ? 'success.main' : 'text.primary'
          }}
        >
          {totalCount === 0 ? t('mailTemplate.campaigns.progress.loading') : `${processedCount}/${totalCount} (${Math.floor(progress)}%)`}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            background: isComplete
              ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
              : statusType === 'preparing'
                ? 'linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)'
                : 'linear-gradient(90deg, #2196f3 0%, #64b5f6 100%)'
          }
        }}
      />

    </Box>
  )
}

export default CampaignProgressBar
